import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  MessageCircle,
  Send,
  ExternalLink,
} from "lucide-react";
import PageHero from "@/components/PageHero";
import { COMPANY, IMAGES } from "@/data/site";

const INFO = [
  {
    icon: MapPin,
    label: "Address",
    value: COMPANY.address,
    testid: "contact-address",
  },
  {
    icon: Phone,
    label: "Phone",
    value: COMPANY.phone,
    href: `tel:${COMPANY.phoneRaw}`,
    testid: "contact-phone",
  },
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: COMPANY.whatsapp,
    href: `https://wa.me/${COMPANY.whatsappRaw}`,
    testid: "contact-whatsapp",
  },
  {
    icon: Mail,
    label: "Email",
    value: COMPANY.email,
    href: `mailto:${COMPANY.email}`,
    testid: "contact-email",
  },
  {
    icon: Clock,
    label: "Operational Hours",
    value: COMPANY.hours,
    testid: "contact-hours",
  },
];

export default function Contact() {
  const [form, setForm] = useState({
    nama: "",
    perusahaan: "",
    email: "",
    hp: "",
    tagihan: "",
    pesan: "",
  });

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.nama || !form.email || !form.pesan) {
      toast.error("Mohon lengkapi nama, email, dan pesan Anda.");
      return;
    }
    toast.success("Terima kasih! Permintaan konsultasi Anda telah kami terima.", {
      description: "Tim AJE akan menghubungi Anda dalam 1×24 jam kerja.",
    });
    setForm({ nama: "", perusahaan: "", email: "", hp: "", tagihan: "", pesan: "" });
  };

  const field =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20";

  return (
    <div data-testid="page-contact">
      <PageHero
        crumb="Contact"
        eyebrow="Contact Us"
        title="Let's Bring Your Solar Project to Life"
        desc="Share your needs — our experts are ready to provide free consultation and the best solutions."
        image={IMAGES.engineer1}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid gap-10 lg:grid-cols-5">
          {/* INFO */}
          <div className="lg:col-span-2 space-y-5">
            {INFO.map((it) => {
              const Inner = (
                <div className="flex gap-4">
                  <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-900">
                    <it.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-emerald-600">
                      {it.label}
                    </div>
                    <div className="mt-1 text-slate-700">{it.value}</div>
                  </div>
                </div>
              );
              return it.href ? (
                <a
                  key={it.label}
                  href={it.href}
                  target={it.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={it.testid}
                  className="block rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-colors hover:border-emerald-500/40"
                >
                  {Inner}
                </a>
              ) : (
                <div
                  key={it.label}
                  data-testid={it.testid}
                  className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm"
                >
                  {Inner}
                </div>
              );
            })}

            <a
              href="https://maps.app.goo.gl/CqEQTNp5SZrTjz9E6"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-map-link"
              className="group relative block h-48 overflow-hidden rounded-2xl border border-slate-100 shadow-sm"
            >
              <img
                src={IMAGES.officeAerial}
                alt="Lokasi kantor AJE"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-blue-900/40" />
              <span className="absolute inset-0 flex items-center justify-center gap-2 font-semibold text-white">
                <MapPin className="h-5 w-5" /> View On Google Maps
                <ExternalLink className="h-4 w-4" />
              </span>
            </a>
          </div>

          {/* FORM */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-3 rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-xl shadow-blue-900/5"
          >
            <h2 className="font-display text-2xl font-bold text-slate-900">
              Project Consultation Form
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Fill out the form below, and we will contact you shortly.
            </p>
            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Full Name *
                  </label>
                  <input
                    name="nama"
                    value={form.nama}
                    onChange={onChange}
                    data-testid="contact-input-nama"
                    className={field}
                    placeholder="Your Name"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Company
                  </label>
                  <input
                    name="perusahaan"
                    value={form.perusahaan}
                    onChange={onChange}
                    data-testid="contact-input-perusahaan"
                    className={field}
                    placeholder="Company Name"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="contact-input-email"
                    className={field}
                    placeholder="email@perusahaan.com"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    No. HP / WhatsApp
                  </label>
                  <input
                    name="hp"
                    value={form.hp}
                    onChange={onChange}
                    data-testid="contact-input-hp"
                    className={field}
                    placeholder="08xxxxxxxxxx"
                  />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Estimated Electricity Bill / Month
                </label>
                <select
                  name="tagihan"
                  value={form.tagihan}
                  onChange={onChange}
                  data-testid="contact-input-tagihan"
                  className={field}
                >
                  <option value="">Select Range</option>
                  <option>&lt; Rp 25 Juta</option>
                  <option>Rp 25 – 100 Juta</option>
                  <option>Rp 100 – 300 Juta</option>
                  <option>&gt; Rp 300 Juta</option>
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                  Project Details / Message *
                </label>
                <textarea
                  name="pesan"
                  value={form.pesan}
                  onChange={onChange}
                  rows={4}
                  data-testid="contact-input-pesan"
                  className={field}
                  placeholder="Describe your project needs..."
                />
              </div>
              <button
                type="submit"
                data-testid="contact-form-submit-button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue-900 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-900/20 transition-all hover:bg-blue-800 hover:-translate-y-0.5"
              >
                Submit Consultation Request <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
